import { Outlet, useLoaderData, useNavigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Toaster } from 'sonner';
import type { RootLoaderData } from './lib/loaders';
import { queryClient } from './lib/queryClient';
import { configureHowler } from './lib/audio-context';
import { LanguageProvider } from './i18n/LanguageContext';

function App() {
  const { session } = useLoaderData() as RootLoaderData;
  const navigate = useNavigate();

  // Check for auth tokens in URL (from email verification/OAuth)
  // Handles both PKCE flow (?code=) and legacy implicit flow (#access_token=)
  useEffect(() => {
    // Skip if already on the callback page
    if (window.location.pathname.includes('/auth/callback')) return;

    // Check for legacy implicit flow tokens in hash
    const hash = window.location.hash;
    if (hash && hash.includes('access_token')) {
      navigate('/auth/callback' + hash, { replace: true });
      return;
    }

    // Check for PKCE code in query params (Supabase v2+ default)
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      navigate('/auth/callback?code=' + encodeURIComponent(code), { replace: true });
    }
  }, [navigate]);

  // Configure Howler.js on app mount
  useEffect(() => {
    configureHowler();
  }, []);

  return (
    <LanguageProvider>
      <QueryClientProvider client={queryClient}>
        <div className="min-h-screen bg-bg-primary text-text-primary">
          <Outlet context={{ session }} />
          <Toaster
            position="top-center"
            richColors
            duration={2000}
            toastOptions={{
              style: {
                fontWeight: 600,
                fontSize: '15px',
              },
            }}
          />
        </div>
      </QueryClientProvider>
    </LanguageProvider>
  );
}

export default App;
